<template>
    <div v-if="!canRead" class="main-data-organization">
        <div class="no-permission-message">
            Sie haben keine Berechtigung, Stammdaten zu öffnen.
        </div>
    </div>

    <div v-else class="main-data-organization">
        <div class="main-data-organization-o">

            <DxForm class="main-data-organization-form"
                    :form-data="organization"
                    :show-validation-summary="true"
                    label-mode="outside"
                    label-location="top"
                    :col-count="8">
                <DxSimpleItem data-field="name"
                              :label="{text:'Firma'}"
                              :col-span="3"
                              :editor-options="{ readOnly: !canWrite }">
                </DxSimpleItem>

                <DxSimpleItem data-field="streetAndNr"
                              :label="{text:'Straße / Nr.'}"
                              :col-span="2"
                              :editor-options="{ readOnly: !canWrite }">
                </DxSimpleItem>

                <DxSimpleItem data-field="zipCode"
                              :label="{text:'PLZ'}"
                              :col-span="1"
                              :editor-options="{ readOnly: !canWrite }">
                </DxSimpleItem>

                <DxSimpleItem data-field="city"
                              :label="{text:'Ort'}"
                              :col-span="2"
                              :editor-options="{ readOnly: !canWrite }">
                </DxSimpleItem>

                <DxSimpleItem data-field="eMail"
                              :label="{text:'E-Mail'}"
                              :validationRules="[{type: 'email'}]"
                              :col-span="3"
                              :editor-options="{ readOnly: !canWrite }">
                </DxSimpleItem>
                <DxSimpleItem data-field="tradeId" :label="{text:'Gewerk'}" :col-span="2">
                    <template #default>
                        <ListLookup v-if="organization" v-model:value="organization.tradeId" list="project_trades" :allow-empty="true" min-width="280px"></ListLookup>
                    </template>
                </DxSimpleItem>
                <DxSimpleItem :col-span="2">
                    <div>
                        <DxButton class="duplicate"
                                  v-if="duplicates && duplicates.length"
                                  icon="copy"
                                  :text="`Duplikate Anzeigen (${duplicates.length})`"
                                  @click="showDupPopup = true" />
                    </div>
                </DxSimpleItem>

                <DxSimpleItem :col-span="3">
                    <div class="vertec-logo-contain">
                        <div v-if="organization && organization.externalId" class="vertec-logo"></div>
                    </div>
                </DxSimpleItem>
            </DxForm>

            <DxDataGrid class="main-data-organization-grid"
                        :focused-row-enabled="true"
                        :focused-row-key="focusedRowKey"
                        :auto-navigate-to-focused-row="true"
                        key-expr="_id"
                        :data-source="contacts"
                        @saving="saving"
                        :pager="{showInfo:true}"
                        @toolbar-preparing="$root.addGridTitle($event,'Ansprechpartner')">
                <template #salutationTemplate="{ data: cellInfo }">
                    <ListLookup list="contact_salutation"
                                :value="cellInfo.value"
                                :on-value-changed="cellInfo.setValue"
                                :view="!canWrite"></ListLookup>
                </template>

                <template #salutationTemplateView="{ data: cellInfo }">
                    <ListLookup list="contact_salutation"
                                :value="cellInfo.value"
                                :on-value-changed="cellInfo.setValue"
                                :view="true"></ListLookup>
                </template>

                <DxColumn data-field="_id" :visible="false" :allowSearch="false"></DxColumn>
                <DxColumn data-field="salutationId" edit-cell-template="salutationTemplate" cell-template="salutationTemplateView" caption="Anrede" />
                <DxColumn data-field="firstName" caption="Vorname" />
                <DxColumn data-field="lastName" caption="Nachname" />
                <DxColumn data-field="eMail" caption="E-Mail" :validationRules="[{type: 'email'}]" />
                <DxColumn data-field="phoneNumber" caption="Telefon" :validationRules="[{type: 'pattern',pattern:'^\\+?[0-9\\- ]*$'}]" />
                <DxColumn data-field="mobileNumber" caption="Mobil" :validationRules="[{type: 'pattern',pattern:'^\\+?[0-9\\- ]*$'}]" />
                <DxColumn data-field="externalId" edit-cell-template="vertecLogo" cell-template="vertecLogo" caption="" width="35px" />

                <template #vertecLogo="{ data: cellInfo }">
                    <div class="vertec-logo-contain vertec-logo-small">
                        <div v-if="!!cellInfo.value" class="vertec-logo"></div>
                    </div>
                </template>

                <DxEditing :allow-updating="canWrite"
                           :allow-deleting="canDelete"
                           :allow-adding="canWrite"
                           mode="batch"
                           v-model:changes="changes"
                           new-row-position="last"
                           :useIcons="true" />

                <DxSearchPanel :visible="true" />
            </DxDataGrid>
        </div>

        <DxDataGrid class="main-data-projects-grid"
                    key-expr="_id"
                    :data-source="involvedProjects"
                    @row-click="rowClick"
                    @toolbar-preparing="$root.addGridTitle($event,'Projektbeteiligung')">
            <DxColumn data-field="_id" :visible="false" :allowSearch="false"></DxColumn>
            <DxColumn data-field="number" caption="Projektnummer" />
        </DxDataGrid>

        <DxPopup :visible="showDupPopup"
                 :show-title="true"
                 title="Duplikate"
                 :width="1000"
                 :height="800"
                 :drag-enabled="true"
                 :close-on-outside-click="true"
                 @hiding="showDupPopup = false">
            <DxDataGrid :data-source="duplicates"
                        @row-dbl-click="showDupOrg">
                <DxColumn data-field="name" caption="Firma" />
                <DxColumn data-field="streetAndNr" caption="Straße / Nr." />
                <DxColumn data-field="zipCode" caption="PLZ" />
                <DxColumn data-field="city" caption="Ort" />
                <DxColumn data-field="eMail" caption="E-Mail" />
                <DxColumn data-field="externalId" caption="Aus Vertec" :calculate-cell-value="e=>!!e.externalId" />
                <DxColumn caption="Zusammenführen" cell-template="merge"></DxColumn>

                <template #merge="{data}">
                    <div>
                        <DxButton v-show="canWrite && (!data.data.externalId || !organization.externalId)"
                                  icon="login"
                                  @click="showMerge(data.data)"></DxButton>
                    </div>
                </template>
            </DxDataGrid>
        </DxPopup>

        <DxPopup :visible="showMergePopup"
                 :show-title="true"
                 title="Zusammenführen"
                 :width="1000"
                 :height="800"
                 :drag-enabled="true"
                 :close-on-outside-click="true"
                 @hiding="showMergePopup = false">
            <div style="height:100%;display:grid;grid-template-rows: minmax(0,1fr);">
                <organization-merge v-if="showMergePopup&&organization&&organizationIdTwo"
                                    :organization-id-one="organization?._id"
                                    :organization-id-two="organizationIdTwo"></organization-merge>
            </div>
        </DxPopup>
    </div>
</template>

<script lang="js">
    import { defineComponent } from 'vue';
    import DxDataGrid, {
        DxColumn,
        DxEditing,
        DxSearchPanel
    } from 'devextreme-vue/data-grid';
    import DxForm, {
        DxSimpleItem
    } from 'devextreme-vue/form';
    import DxButton from 'devextreme-vue/button';
    import ListLookup from '../components/list-lookup.vue';
    import OrganizationMerge from '../components/organization-merge.vue';
    import organizationApi from '../services/OrganizationApi';
    import { addProcess } from '../services/Processor';
    import { DxPopup } from 'devextreme-vue/popup';
    import { cleanString, getDupeCheckFilters } from '../services/Util';
    import meApi from '../services/MeApi';
    import AppPermissions from '../constants/AppPermissions';

    export default defineComponent({
        components: {
            DxForm,
            DxSimpleItem,
            DxButton,
            DxDataGrid,
            DxColumn,
            DxEditing,
            ListLookup,
            DxSearchPanel,
            DxPopup,
            OrganizationMerge
        },
        data() {
            return {
                contacts: [],
                involvedProjects: [],
                formData: null,
                organization: null,
                process: null,
                changes: [],
                focusedRowKey: null,
                duplicates: [],
                showDupPopup: false,
                showMergePopup: false,
                organizationIdOne: null,
                organizationIdTwo: null
            };
        },
        computed: {
            canRead() {
                return meApi.hasPermissionSync(AppPermissions.MasterData.Read);
            },
            canWrite() {
                return meApi.hasPermissionSync(AppPermissions.MasterData.Write);
            },
            canDelete() {
                return meApi.hasPermissionSync(AppPermissions.MasterData.Delete);
            }
        },
        watch: {
            '$route.params.id'() {
                this.fetchData();
            },
            '$route.params.subId'() {
                this.focusedRowKey = this.$route.params.subId;
            },
            async 'organization.name'() {
                if (this.canRead)
                    this.reloadDupes();
            },
            async 'organization.city'() {
                if (this.canRead)
                    this.reloadDupes();
            },
            changes() {
                if (this.process)
                    this.process();
                if (this.changes.length > 0) {
                    this.process = addProcess("Changing DataGrid");
                }
            }
        },
        async created() {
            if (!this.canRead)
                return;

            await this.fetchData();
        },
        methods: {
            async fetchData() {
                if (!this.canRead || !this.$route.params?.id) {
                    return;
                }

                [this.contacts, this.organization, this.involvedProjects] = await Promise.all([
                    organizationApi.getOrganizationContacts(this.$route.params.id),
                    organizationApi.get(this.$route.params.id),
                    organizationApi.getProjectsInvolved(this.$route.params.id)
                ]);

                if (this.$route.params.subId) {
                    this.focusedRowKey = this.$route.params.subId;
                }
            },
            async reloadDupes() {
                let name = this.organization.name;
                if (!cleanString(name)) {
                    this.duplicates = [];
                    return;
                }

                let searchStrings = getDupeCheckFilters(name, this.organization.city)
                let filteredResults = await Promise.all(
                    searchStrings.map(s =>
                        organizationApi.search(s).then(r => r.organizations.filter(org => org._id !== this.$route.params.id))
                    )
                ).then(m => m.flat());

                this.duplicates = filteredResults;
            },
            async rowClick(e) {
                window.open(`/Projects/${e.key}`, '_blank')
            },
            async saving(e) {
                if (!this.canWrite && !this.canDelete) {
                    e.cancel = true;
                    return;
                }

                for (let change of e.changes) {
                    let item = structuredClone(this.contacts.find(t => t._id === change.key));

                    if (change.type === "update") {
                        if (!this.canWrite) {
                            e.cancel = true;
                            return;
                        }
                        Object.assign(item, change.data)
                        await organizationApi.saveContact(item);
                    }
                    else if (change.type === "insert") {
                        if (!this.canWrite) {
                            e.cancel = true;
                            return;
                        }
                        item = change.data;
                        item.organizationId = this.$route.params.id;
                        await organizationApi.saveContact(item);
                    }
                    else if (change.type === "remove") {
                        if (!this.canDelete) {
                            e.cancel = true;
                            return;
                        }
                        await organizationApi.deleteContact(change.key)
                    }
                    else {
                        console.error("DONT KNOW", change.type)
                    }
                }

                if (this.process)
                    this.process();
            },
            showMerge(e) {
                if (!this.canWrite)
                    return;

                this.organizationIdTwo = e._id
                this.showMergePopup = true;
            }
        },
    });
</script>

<style>
    .main-data-organization {
        display: grid;
        grid-template-columns: minmax(0,3fr) 1fr;
        grid-template-rows: minmax(0,1fr);
        grid-gap: 10px;
    }

    .main-data-organization-o {
        display: grid;
        grid-template-columns: 1fr;
        grid-template-rows: max-content minmax(0,1fr) minmax(0,max-content);
        grid-gap: 10px;
    }

    .main-data-organization-grid .dx-datagrid {
        padding: 10px;
        background-color: #2a2a2a;
    }

    .main-data-projects-grid .dx-datagrid {
        padding: 10px;
        background-color: #2a2a2a;
    }

    .duplicate {
        margin-top: 26px;
        background: #FF000080;
    }

    .vertec-logo {
        position: absolute;
        inset: 0;
        background-image: url(/public/vertec_logo.jfif);
        background-size: contain;
        background-repeat: no-repeat;
        background-position: center right;
    }

    .vertec-logo-contain {
        position: relative;
        height: 62px;
    }

        .vertec-logo-contain.vertec-logo-small {
            position: relative;
            height: 19px;
        }

    .no-permission-message {
        padding: 20px;
        background-color: #2a2a2a;
    }
</style>